import { useState } from 'react';
import { Copy, Download, Share2 } from 'lucide-react';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { exportarRutina, type RutinaExportada } from '@/db/rutinas-archivo';
import { mensajeDeError } from '@/db/tx';
import { compartirArchivo, copiarAlPortapapeles, descargarArchivo, puedeCompartirArchivos } from '@/lib/file-transfer';

/**
 * Compartir una rutina como archivo (Req. 27.1).
 *
 * En dos pasos, como el respaldo: primero se prepara el archivo y luego se
 * entrega, porque en iOS compartir y copiar solo funcionan si se llaman en el
 * mismo toque, sin esperas antes (design.md, 10.5).
 */
export function CompartirRutina({ routineId }: { routineId: string }) {
  const [listo, setListo] = useState<RutinaExportada | null>(null);
  const [preparando, setPreparando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'info' | 'aviso'; titulo: string; texto: string } | null>(null);
  const puedeCompartir = puedeCompartirArchivos();

  async function preparar() {
    if (preparando) return;
    setPreparando(true);
    setError(null);
    setAviso(null);
    try {
      setListo(await exportarRutina(routineId));
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setPreparando(false);
    }
  }

  async function compartir(r: RutinaExportada) {
    const compartido = await compartirArchivo(r.nombre, r.texto, 'application/json', 'Rutina de DuoFit');
    setAviso(
      compartido
        ? { tono: 'info', titulo: 'Compartida', texto: `${r.nombre}, desde el menú del iPhone.` }
        : {
            tono: 'aviso',
            titulo: 'No se compartió',
            texto: 'Se cerró el menú o el iPhone no lo permitió. "Guardar en Archivos" es la vía más confiable.',
          },
    );
  }

  async function copiar(r: RutinaExportada) {
    const copiado = await copiarAlPortapapeles(r.texto);
    setAviso(
      copiado
        ? { tono: 'info', titulo: 'Copiada como texto', texto: 'Pégala en el chat o en una nota; se importa igual.' }
        : { tono: 'aviso', titulo: 'No se pudo copiar', texto: 'Usa "Guardar en Archivos" o "Compartir".' },
    );
  }

  if (listo === null) {
    return (
      <div className="grid gap-2">
        <Button variante="fantasma" icono={Share2} disabled={preparando} onClick={() => void preparar()}>
          {preparando ? 'Preparando…' : 'Compartir esta rutina'}
        </Button>
        {error !== null && (
          <Banner tono="alerta" titulo="No se pudo preparar">
            {error}
          </Banner>
        )}
      </div>
    );
  }

  return (
    <Card className="space-y-4">
      <div>
        <span className="antetitulo">Rutina lista</span>
        <p className="mt-2 text-[15px] leading-snug text-texto">{listo.nombre}</p>
        <p className="mt-1 text-[13px] leading-relaxed text-texto-suave">
          Lleva los {listo.ejercicios} ejercicios con su objetivo, descanso e indicaciones. No lleva tus cargas, ni tu
          historial, ni tus datos.
          {listo.sinEjercicio > 0 &&
            ` ${listo.sinEjercicio === 1 ? 'Un ejercicio' : `${listo.sinEjercicio} ejercicios`} no se pudo incluir porque ya no existe en tus ejercicios.`}
        </p>
      </div>
      <div className="grid gap-2">
        <Button icono={Download} onClick={() => descargarArchivo(listo.nombre, listo.texto)}>
          Guardar en Archivos
        </Button>
        {puedeCompartir && (
          <Button variante="secundario" icono={Share2} onClick={() => void compartir(listo)}>
            Compartir
          </Button>
        )}
        <Button variante="fantasma" icono={Copy} onClick={() => void copiar(listo)}>
          Copiar como texto
        </Button>
      </div>
      {aviso !== null && (
        <Banner tono={aviso.tono} titulo={aviso.titulo}>
          {aviso.texto}
        </Banner>
      )}
      <p className="text-[12px] leading-relaxed text-texto-suave">
        Quien la reciba la abre en su DuoFit con Gym → Herramientas → "Traer rutina". Verá qué trae antes de guardarla.
      </p>
    </Card>
  );
}
